/**
 * The app's page list, organised into areas (backlog #53, feat/nav-areas). The sidebar's area icon row, the collapsed
 * rail, the mobile tab bar and "More" sheet, and the Ctrl+K palette all read this, and each item is shown only when
 * canAccess(path) allows it (lib/navAccess.tsx). An area with no visible items is hidden.
 *
 * Mapping (also in docs/navigation.md):
 *   Home          Home, Announcements, Kudos, My acknowledgements, Weekly digests · Buildings, Departments, Groups,
 *                 People (each followable: followed ones are listed under their item, t139)
 *   Chat          Channels, ad-hoc meeting rooms and direct messages
 *   People & shifts My shifts, Schedule, Time off, Timesheets (attendance) · People: Departments & groups, Users, Onboarding, Reports
 *   Tasks         Tasks (private ones included), Projects (+ project list), Calendar · Operations: Checklists, Handovers, Issues, Forms
 *   Fleet         Fleet, My runs
 *   Policies      Policies (library, what you have to read), Waiting for me (approvals, t563u)
 *   Pinned        Assistant, Help Centre, Settings (always at the bottom). Search is Ctrl+K and the top bar.
 */
import {
  Award, BarChart3, Building2, Contact, CheckSquare, CalendarClock, CalendarDays, CalendarRange, ClipboardList, ClipboardSignature, Clock, FolderKanban,
  Handshake, Home, IdCard, LifeBuoy, ListChecks, MapPinned, Megaphone, MessagesSquare, Navigation, Network, Newspaper, Plane, Route, Search, Settings, ShieldCheck, Siren, Sparkles, Truck, UserCog, UserPlus, UsersRound, Wrench, DoorOpen, GraduationCap, LayoutDashboard, ScrollText, Stamp,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem { to: string; label: string; icon: LucideIcon; end?: boolean; keywords?: string[] }
/** `divider`: an untitled group drawn after a hairline instead of a heading (t141u). */
export interface NavGroup { id: string; title: string | null; divider?: boolean; items: NavItem[] }
export type AreaId = 'home' | 'chat' | 'shifts' | 'tasks' | 'fleet';
export interface NavArea {
  id: AreaId; label: string; icon: LucideIcon;
  /** Alt+<key> switches to the area. */
  key: string;
  groups: NavGroup[];
  /** Extra route prefixes that belong to the area (pages reached from its items). */
  prefixes: string[];
  /** Where the area opens when you haven't been there yet and none of its items is first (Chat). */
  landing?: string;
}

const HOME: NavItem = { to: '/', label: 'Home', icon: Home, end: true, keywords: ['dashboard'] };
export const NAV_CHAT: NavItem = { to: '/chat', label: 'Chat', icon: MessagesSquare, keywords: ['channels', 'messages', 'dms'] };

export const NAV_AREAS: NavArea[] = [
  { id: 'home', label: 'Home', icon: Home, key: '1', prefixes: ['/buildings/'], groups: [
    { id: 'home', title: null, items: [
      HOME,
      { to: '/dashboard', label: 'Manager dashboard', icon: LayoutDashboard, keywords: ['dashboard', 'overview', 'kpi', 'who is in', 'sla', 'labour', 'hours', 'at a glance'] },
      // t554u: till sales by period and building (sales.view at a building with tills).
      { to: '/sales', label: 'Store sales', icon: BarChart3, keywords: ['sales', 'takings', 'revenue', 'tills', 'transactions', 'top sellers', 'odoo', 'atv'] },
      { to: '/announcements', label: 'Announcements', icon: Megaphone, keywords: ['news', 'urgent'] },
      { to: '/kudos', label: 'Kudos', icon: Award, keywords: ['thanks', 'recognition', 'birthdays'] },
      { to: '/acknowledgements', label: 'My acknowledgements', icon: ShieldCheck, keywords: ['acknowledgements', 'read', 'confirm', 'policies'] },
      { to: '/digests', label: 'Weekly digests', icon: Newspaper, keywords: ['summary'] },
    ] },
    // t139: things you can follow; followed ones are listed under each item.
    { id: 'follow', title: null, divider: true, items: [
      { to: '/buildings', label: 'Buildings', icon: Building2, end: true, keywords: ['locations', 'sites', 'stores'] },
      { to: '/departments', label: 'Departments', icon: Network, keywords: ['teams', 'follow'] },
      { to: '/custom-groups', label: 'Groups', icon: UsersRound, keywords: ['groups', 'follow'] },
      { to: '/people', label: 'People', icon: Contact, keywords: ['directory', 'colleagues', 'staff', 'follow'] },
    ] },
  ] },
  { id: 'chat', label: 'Chat', icon: MessagesSquare, key: '2', prefixes: ['/c/', '/chat', '/channels', '/meeting-rooms', '/rooms'], landing: '/chat', groups: [] },
  { id: 'shifts', label: 'People & shifts', icon: CalendarClock, key: '3', prefixes: ['/workforce/'], groups: [
    { id: 'wf', title: null, items: [
      { to: '/workforce/my-shifts', label: 'My shifts', icon: CalendarClock, keywords: ['rota', 'clock in', 'availability', 'my availability'] },
      { to: '/workforce/schedule', label: 'Schedule', icon: CalendarDays, keywords: ['rota', 'planner'] },
      { to: '/workforce/time-off', label: 'Time off', icon: Plane, keywords: ['holiday', 'leave', 'annual leave'] },
      { to: '/workforce/timesheets', label: 'Timesheets', icon: Clock, keywords: ['hours', 'clock', 'attendance', 'who is in'] },
    ] },
    { id: 'people', title: 'People', items: [
      { to: '/groups', label: 'Departments & groups', icon: UsersRound, keywords: ['directory', 'teams', 'groups', 'departments', 'leads', 'colleagues', 'add staff'] },
      { to: '/settings/people/users', label: 'Users', icon: UserCog, keywords: ['users', 'employees', 'accounts', 'contracts', 'invite'] },
      { to: '/onboarding', label: 'Onboarding', icon: UserPlus, keywords: ['new starter', 'induction'] },
      { to: '/training', label: 'Training', icon: GraduationCap, keywords: ['course', 'quiz', 'induction', 'test', 'learning', 'certificate', 'refresher'] },
      { to: '/workforce/reports', label: 'Reports', icon: BarChart3, keywords: ['hours', 'cost', 'bonuses', 'staffing', 'sales'] },
    ] },
  ] },
  // t141u: the area is "Operations"; its id stays 'tasks' (routes, shortcuts, saved prefs).
  { id: 'tasks', label: 'Operations', icon: CheckSquare, key: '4', prefixes: ['/projects/', '/checklists/', '/incidents/', '/forms/', '/workforce/handover', '/equipment', '/visitors', '/policies/'], groups: [
    { id: 'work', title: null, items: [
      { to: '/tasks', label: 'Tasks', icon: CheckSquare, end: true, keywords: ['my tasks', 'assigned', 'no project', 'standalone', 'private', 'to-do', 'todo', 'reminders', 'personal'] },
      { to: '/projects', label: 'Projects', icon: FolderKanban, end: true, keywords: ['tasks', 'boards'] },
      { to: '/calendar', label: 'Calendar', icon: CalendarRange, keywords: ['events', 'birthdays'] },
    ] },
    { id: 'ops', title: null, divider: true, items: [
      { to: '/incidents', label: 'Issues', icon: Siren, keywords: ['incidents', 'tickets', 'report a problem'] },
      { to: '/checklists', label: 'Checklists', icon: ListChecks, keywords: ['opening', 'closing', 'safety'] },
      { to: '/equipment', label: 'Equipment', icon: Wrench, keywords: ['forklift', 'pallet truck', 'loler', 'puwer', 'pre-use check', 'defects', 'service', 'inspection', 'racking'] },
      { to: '/visitors', label: 'Visitors & on site', icon: DoorOpen, keywords: ['visitor sign in', 'contractors', 'who is on site', 'fire', 'roll call', 'evacuation', 'register', 'muster'] },
      { to: '/workforce/handovers', label: 'Handovers', icon: ClipboardSignature, keywords: ['shift notes'] },
      { to: '/forms', label: 'Channel forms', icon: ClipboardList, keywords: ['forms', 'form builder', 'templates', 'submissions'] },
    ] },
    // t563u: policy and document management (versions, approvals, acknowledgements). t642u: in Operations, no longer its own area.
    { id: 'policies', title: null, divider: true, items: [
      { to: '/policies', label: 'Policies', icon: ScrollText, keywords: ['procedures', 'documents', 'handbook', 'acknowledge', 'read and sign', 'health and safety', 'hr policy', 'code of conduct', 'review date'] },
      { to: '/policies/approvals', label: 'Waiting for me', icon: Stamp, keywords: ['approve policy', 'policy review', 'request changes'] },
    ] },
  ] },
  { id: 'fleet', label: 'Fleet', icon: Truck, key: '5', prefixes: ['/fleet/'], groups: [
    { id: 'fleet', title: null, items: [
      { to: '/fleet/dashboard', label: 'Dashboard', icon: LayoutDashboard, keywords: ['fleet dashboard', 'overview', 'today', 'defects', 'walkaround'] },
      { to: '/fleet/journeys', label: 'Journeys', icon: Route, keywords: ['deliveries', 'plan journey', 'runs', 'fleet'] },
      { to: '/fleet/live', label: 'Live map', icon: MapPinned, keywords: ['tracking', 'where is', 'location', 'vehicles', 'gps'] },
      { to: '/fleet/calendar', label: 'Fleet calendar', icon: CalendarDays, keywords: ['mot', 'service', 'due dates'] },
      { to: '/fleet/vehicles', label: 'Vehicles', icon: Truck, keywords: ['vans', 'trucks', 'defects', 'mot', 'tax', 'insurance'] },
      { to: '/fleet/drivers', label: 'Drivers', icon: IdCard, keywords: ['licence', 'cpc', 'points'] },
      { to: '/fleet/hauliers', label: 'Hauliers', icon: Handshake, keywords: ['carriers', 'pallets', 'rates'] },
      { to: '/fleet/reports', label: 'Fleet reports', icon: BarChart3, keywords: ['on time', 'cost', 'mileage'] },
      { to: '/fleet/my-runs', label: 'My runs', icon: Navigation, keywords: ['driver', 'deliveries'] },
    ] },
  ] },
];

export const NAV_SETTINGS: NavItem = { to: '/settings', label: 'Settings', icon: Settings, keywords: ['preferences', 'admin', 'profile'] };
// #61: one entry for the Help Centre and the company knowledge base (/help, /help/kb).
export const NAV_HELP: NavItem = { to: '/help', label: 'Help & knowledge base', icon: LifeBuoy, keywords: ['help', 'guide', 'user guide', 'how to', 'manual', 'support', 'knowledge base', 'kb', 'procedures', 'sop', 'articles'] };
export const NAV_ASSISTANT: NavItem = { to: '/assistant', label: 'Assistant', icon: Sparkles, keywords: ['ai', 'ask', 'gemini'] };
export const NAV_SEARCH: NavItem = { to: '/search', label: 'Search', icon: Search, keywords: ['find', 'messages'] };
/** Pinned at the bottom of the sidebar in every area. */
export const NAV_PINNED: NavItem[] = [NAV_ASSISTANT, NAV_HELP, NAV_SETTINGS];

export const areaItems = (a: NavArea): NavItem[] => a.groups.flatMap(g => g.items);
export const allNavItems = (): NavItem[] => [...NAV_AREAS.flatMap(areaItems), NAV_CHAT, NAV_SEARCH, ...NAV_PINNED];
export const areaById = (id: string | null | undefined) => NAV_AREAS.find(a => a.id === id) ?? null;

const matches = (path: string, to: string, end?: boolean) => (to === '/' ? path === '/' : path === to || (!end && path.startsWith(`${to}/`)));

/**
 * Which area a route belongs to (longest match over every item and extra prefix), or null for pages outside the
 * areas (Settings, Help, Search, Assistant), which keep the last area selected.
 */
export function areaForPath(path: string): AreaId | null {
  let best: { id: AreaId; len: number } | null = null;
  for (const a of NAV_AREAS) {
    const cands = [...areaItems(a).filter(i => matches(path, i.to, i.end)).map(i => i.to.length), ...a.prefixes.filter(p => path === p.replace(/\/$/, '') || path.startsWith(p)).map(p => p.length)];
    for (const len of cands) if (!best || len > best.len) best = { id: a.id, len };
  }
  return best?.id ?? null;
}

/** Areas and items the person can open (Chat always: every member has channels and messages). */
export function visibleAreas(can: (path: string) => boolean): (NavArea & { visible: NavItem[] })[] {
  return NAV_AREAS.map(a => ({ ...a, visible: areaItems(a).filter(i => can(i.to)) }))
    .filter(a => a.id === 'chat' || a.visible.length > 0);
}

/** Where an area opens: the last page you used in it (if you can still open it), else its first item. */
export function areaLanding(a: NavArea & { visible: NavItem[] }, last: string | null | undefined, can: (path: string) => boolean): string {
  if (last && areaForPath(last.split(/[?#]/)[0]) === a.id && can(last.split(/[?#]/)[0])) return last;
  return a.landing ?? a.visible[0]?.to ?? '/';
}

// Remembered per person: the last area, and the last page in each area.
export const areaKey = (userId: string | null | undefined) => `collab.nav.area.${userId ?? 'anon'}`;
export const areaPageKey = (userId: string | null | undefined, id: AreaId) => `collab.nav.area.${userId ?? 'anon'}.${id}`;

/** Alt+1…5 (by key code, so it works on every keyboard layout and on a Mac's Option key). */
export function areaShortcut(e: Pick<KeyboardEvent, 'altKey' | 'ctrlKey' | 'metaKey' | 'shiftKey' | 'code'>): string | null {
  if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return null;
  const m = /^(?:Digit|Numpad)([1-5])$/.exec(e.code);
  return m ? m[1] : null;
}
